import type {
  Scenario,
  ScenarioLight,
  ScenarioOutput,
  ScenarioWithInventory,
  NewScenario,
  ScenarioParameters,
  LocalScenario,
} from '@/types';
import { api } from './api';

const scenarioApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getScenario: builder.query<Scenario, number>({
      query: (id) => `scenarios/${id}`,
    }),
    getScenarios: builder.query<ScenarioLight[], number>({
      query: (inventoryId) => `inventories/${inventoryId}/scenarios`,
    }),
    getAllScenarios: builder.query<ScenarioWithInventory[], void>({
      query: () => `scenarios`,
    }),
    simulation: builder.query<
      ScenarioOutput,
      ScenarioParameters & { scenarioId: number }
    >({
      query: ({ scenarioId, ...parameters }) => ({
        url: `scenarios/${scenarioId}/simulation`,
        method: 'POST',
        body: parameters,
      }),
    }),
    downloadScenario: builder.mutation<
      {},
      ScenarioParameters & { scenarioId: number }
    >({
      query: ({ scenarioId, ...parameters }) => ({
        url: `scenarios/${scenarioId}/download`,
        method: 'POST',
        body: parameters,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        queryFulfilled.then(({ data }: any) => {
          window.location = data.url;
        });
      },
    }),
    newScenario: builder.mutation<Scenario, NewScenario>({
      query: ({ inventoryId, name }) => ({
        url: `inventories/${inventoryId}/scenarios`,
        method: 'POST',
        body: {
          name,
        },
      }),
    }),
    deleteScenario: builder.mutation<
      void,
      { scenarioId: number; inventoryId: number }
    >({
      query: ({ scenarioId }) => ({
        url: `scenarios/${scenarioId}`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ scenarioId, inventoryId }, { dispatch }) => {
        dispatch(
          scenarioApi.util.updateQueryData(
            'getScenarios',
            inventoryId,
            (current) =>
              current.filter((v: ScenarioLight) => v.id != scenarioId),
          ),
        );
      },
    }),
    saveScenario: builder.mutation<Scenario, LocalScenario>({
      query: (scenario) => ({
        url: `scenarios/${scenario.id}`,
        method: 'POST',
        body: scenario,
      }),
      onQueryStarted: async (scenario, { dispatch }) => {
        dispatch(
          scenarioApi.util.updateQueryData(
            'getScenario',
            scenario.id,
            // this is awkward, not all interventions will have id,
            // need to fix types...
            () => scenario as Scenario,
          ),
        );
      },
    }),
  }),
});

export const {
  useGetScenarioQuery,
  useLazyGetScenarioQuery,
  useSimulationQuery,
  useDownloadScenarioMutation,
  useGetScenariosQuery,
  useGetAllScenariosQuery,
  useNewScenarioMutation,
  useSaveScenarioMutation,
  useDeleteScenarioMutation,
} = scenarioApi;
